/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { call, select } from 'redux-saga/effects';
import {
  Seconds,
  Time,
  DestructuredDuration,
  CatchupManifestURLRegex,
  CatchupPadding,
  DurationUtils,
  PaddingMode,
  PlayerPadding,
  Program,
  ProgramPadding,
  ProgramTimes,
  TimeUtils,
  Channel,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
// import { LicensedStream } from '../ui/player';
import { Selectors as ChannelsSelectors } from './playableChannels';

export interface EpgAdjustedData {
  currentStream?: TemporaryLicensedStream | undefined;
  duration: Seconds;
  paddingStart?: Seconds;
  paddingEnd?: Seconds;
}

interface TemporaryLicensedStream {
  id: number;
  uri: string;
  token?: string;
  includesPadding?: boolean;
}

interface SplittedStreamUri {
  begin: string;
  end: string;
  property: string;
  value: Time | undefined;
}

// UTILS

function* getPadding(program: Program): SagaIterator<ProgramPadding> {
  let start: DestructuredDuration | undefined;
  let end: DestructuredDuration | undefined;

  const catchupPadding: CatchupPadding | undefined = yield select(Ducks.Dms.Selectors.getCatchupPadding);
  const relatedChannel: Channel | undefined = yield select(ChannelsSelectors.getChannelByEpgId, program.channelEpgId);

  const { padding } = program;
  const { programPadding } = relatedChannel || {};
  const { globalPadding } = catchupPadding || {};

  if (padding?.start !== undefined) {
    start = padding.start;
  } else if (programPadding?.start !== undefined) {
    start = programPadding.start;
  } else if (globalPadding?.start !== undefined) {
    start = globalPadding.start;
  }

  if (padding?.end !== undefined) {
    end = padding.end;
  } else if (programPadding?.end !== undefined) {
    end = programPadding.end;
  } else if (globalPadding?.end !== undefined) {
    end = globalPadding.end;
  }

  return {
    start,
    end,
  };
}

function* getPlayerPadding(program: Program): SagaIterator<PlayerPadding> {
  const { start, end }: ProgramPadding = yield call(getPadding, program);

  return {
    start: start ? DurationUtils.destructuredDurationtoSeconds(start) : 0,
    end: end ? DurationUtils.destructuredDurationtoSeconds(end) : 0,
  };
}

// PaddingMode === MANIFEST_REGEX

function splitStreamUri(uri: string, regex: RegExp): SplittedStreamUri | undefined {
  const match = uri.match(regex);
  if (match?.length === 3 && match.index !== undefined) {
    const date = TimeUtils.fromIsoString(match[2]);
    return {
      begin: uri.substring(0, match.index),
      end: uri.substring(match.index + match[0].length, uri.length),
      property: match[1],
      value: date,
    };
  }
  return undefined;
}

function addPaddingToProperty(uri: string, regex: RegExp, padding: Seconds): string | undefined {
  const splittedUri = splitStreamUri(uri, regex);
  if (splittedUri) {
    const { begin, end, property, value } = splittedUri;
    if (value) {
      let paddedValue = TimeUtils.addSeconds(value, padding);
      paddedValue = TimeUtils.isAfterNow(paddedValue) ? TimeUtils.now() : paddedValue;
      return `${begin}${property}${TimeUtils.toIsoStringSeconds(paddedValue)}${end}`;
    }
    // if the rexeg match but the value is not a date, don't change this property
    return uri;
  }
  return undefined;
}

function applyPaddingToStream(
  stream: TemporaryLicensedStream,
  paddingStart: Seconds = 0,
  paddingEnd: Seconds = 0,
  manifestUrlRegex: CatchupManifestURLRegex | undefined
): TemporaryLicensedStream | undefined {
  if (manifestUrlRegex === undefined) {
    return undefined;
  }

  const { uri } = stream;
  let paddedUri = addPaddingToProperty(uri, manifestUrlRegex.start, -1 * paddingStart);
  // Only change the uri if both regexs from start and end match
  if (!paddedUri) {
    return undefined;
  }

  paddedUri = addPaddingToProperty(paddedUri, manifestUrlRegex.end, paddingEnd);
  if (!paddedUri) {
    return undefined;
  }

  return {
    ...stream,
    uri: paddedUri,
    includesPadding: true,
  };
}

function* getEpgAdjustedStreamData(
  program: Program,
  stream: TemporaryLicensedStream
): SagaIterator<EpgAdjustedData | undefined> {
  const { start, end }: PlayerPadding = yield call(getPlayerPadding, program);
  const duration = (program.durationValue || 0) * TimeUtils.MIN_SECS;
  if (start || end) {
    const urlRegex: CatchupManifestURLRegex | undefined = yield select(Ducks.Dms.Selectors.getCatchupManifestUrlRegex);
    const paddedStream = applyPaddingToStream(stream, start, end, urlRegex);
    if (paddedStream) {
      return {
        paddingStart: start,
        paddingEnd: end,
        duration: duration + start + end,
        currentStream: paddedStream,
      };
    }
  }
  return undefined;
}

// PaddingMode === GET_PLAYBACK_CONTEXT

export function* getStartAndEndTimes(program: Program): SagaIterator<ProgramTimes> {
  const { start, end }: ProgramPadding = yield call(getPadding, program);
  const startDate: Time = start ? TimeUtils.subtractDestructuredDuration(program.startDate, start) : program.startDate;
  const endDate: Time = end ? TimeUtils.addDestructuredDuration(program.endDate, end) : program.endDate;
  return {
    start: TimeUtils.toIsoStringSeconds(startDate),
    end: TimeUtils.toIsoStringSeconds(endDate),
  };
}

function* getEpgAdjustedPlaybackContextData(
  program: Program,
  stream: TemporaryLicensedStream
): SagaIterator<EpgAdjustedData | undefined> {
  const { start, end }: PlayerPadding = yield call(getPlayerPadding, program);
  const duration = (program.durationValue || 0) * TimeUtils.MIN_SECS;
  if (start || end) {
    return {
      paddingStart: start,
      paddingEnd: end,
      duration: duration + start + end,
      currentStream: { ...stream, includesPadding: true },
    };
  }
  return undefined;
}

// ALL

export function* getCatchupPaddingMode(): SagaIterator<PaddingMode> {
  const catchupPadding: CatchupPadding | undefined = yield select(Ducks.Dms.Selectors.getCatchupPadding);
  return catchupPadding?.paddingMode || PaddingMode.OFF;
}

export function* getEpgPaddedPlayableData(
  program: Program,
  stream: TemporaryLicensedStream
): SagaIterator<EpgAdjustedData | undefined> {
  const paddingMode: PaddingMode = yield call(getCatchupPaddingMode);

  switch (paddingMode) {
    case PaddingMode.GET_PLAYBACK_CONTEXT: {
      return yield call(getEpgAdjustedPlaybackContextData, program, stream);
    }
    case PaddingMode.MANIFEST_REGEX: {
      return yield call(getEpgAdjustedStreamData, program, stream);
    }
    default: {
      return undefined;
    }
  }
}
